import { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useSettings } from '@/hooks/useSettings';
import { useUnifiedDirection } from '@/hooks/useUnifiedDirection';

type Language = 'ar' | 'en';

interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string, arText?: string, enText?: string) => string;
  dir: 'rtl' | 'ltr';
  isRTL: boolean;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};

interface LanguageProviderProps {
  children: ReactNode;
}

export const LanguageProvider = ({ children }: LanguageProviderProps) => {
  // Initialize from localStorage synchronously to minimize flicker
  const [language, setLanguage] = useState<Language>(() => {
    try {
      const stored = localStorage.getItem('language') as Language | null;
      if (stored === 'ar' || stored === 'en') return stored;
    } catch { /* noop */ }
    // Default to 'ar' to match index.html early lang/dir and avoid flash
    return 'ar';
  });

  // Fetch settings to determine default language if none stored
  const { data: settings } = useSettings();

  useEffect(() => {
    // If user has not chosen a language yet, try default from DB settings
    const stored = localStorage.getItem('language') as Language | null;
    if (stored === 'ar' || stored === 'en') return;
    if (settings && settings.length) {
      const possibleKeys = ['site_default_language', 'default_language', 'language_default'];
      const foundKey = possibleKeys.find(k => settings.some(s => s.key === k));
      const defaultSetting = settings.find(s => s.key === foundKey);
      const raw = (defaultSetting?.value_en || defaultSetting?.value_ar || 'en').toLowerCase();
      const dbDefault: Language = raw === 'ar' ? 'ar' : 'en';
      setLanguage(dbDefault);
      try { localStorage.setItem('language', dbDefault); } catch { /* noop */ }
    }
  }, [settings]);

  useEffect(() => {
    // Persist and update document title only - dir/lang handled by useUnifiedDirection
    try { localStorage.setItem('language', language); } catch { /* noop */ }
    const titleKey = language === 'ar' ? 'مجموعة أباسك' : 'Al Subait Group';
    document.title = titleKey;
  }, [language]);

  // Cross-tab and in-app sync: listen to storage and custom events
  useEffect(() => {
    // One-time URL param override (?lang=ar|en)
  try {
      const url = new URL(window.location.href);
      const qp = url.searchParams.get('lang');
      if (qp === 'ar' || qp === 'en') {
        // persist immediately to avoid races with settings effect
    try { localStorage.setItem('language', qp); } catch { /* noop */ }
        setLanguage(qp);
        url.searchParams.delete('lang');
        window.history.replaceState({}, document.title, url.toString());
      }
  } catch { /* noop */ }

    const onStorage = (e: StorageEvent) => {
      if (e.key === 'language' && (e.newValue === 'ar' || e.newValue === 'en')) {
        setLanguage(e.newValue as Language);
      }
    };
    const onCustom = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      const lang = detail?.language as Language | undefined;
      if (lang === 'ar' || lang === 'en') {
        setLanguage(lang);
      }
    };
    window.addEventListener('storage', onStorage);
    window.addEventListener('site:languageChanged', onCustom as EventListener);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('site:languageChanged', onCustom as EventListener);
    };
  }, []);

  const handleSetLanguage = (lang: Language) => {
    if (lang !== 'ar' && lang !== 'en') return;
    setLanguage(lang);
    try { localStorage.setItem('language', lang); } catch { /* noop */ }
    // Optional: broadcast change for listeners that don't consume context directly
    window.dispatchEvent(new CustomEvent('site:languageChanged', { detail: { language: lang } }));
  };

  // Simple translation function
  const t = (key: string, arText?: string, enText?: string): string => {
    if (arText && enText) {
      return language === 'ar' ? arText : enText;
    }
    return key;
  };

  // Use unified direction hook
  const { dir, isRTL } = useUnifiedDirection({ 
    language,
    enableFeatureFlag: true 
  });

  return (
    <LanguageContext.Provider
      value={{
        language,
        setLanguage: handleSetLanguage,
        t,
        dir,
        isRTL,
      }}
    >
      <div dir={dir} className={`${language === 'ar' ? 'font-arabic-body' : 'font-english-body'}`}>
        {children}
      </div>
    </LanguageContext.Provider>
  );
};

// Admin-only Arabic language provider: forces Arabic UI in the admin area
export const AdminLanguageProvider = ({ children }: { children: ReactNode }) => {
  const language: Language = 'ar';
  
  // Use unified direction for admin with forced Arabic
  const { dir, isRTL } = useUnifiedDirection({ 
    language: 'ar',
    forceDirection: 'rtl',
    enableFeatureFlag: true 
  });

  // No-op setter to keep admin UI strictly Arabic
  const setLanguage = () => {};

  const t = (key: string, arText?: string, _enText?: string) => {
    if (arText) return arText;
    return key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t, dir, isRTL }}>
      <div className="font-arabic-body" dir="rtl" lang="ar">
        {children}
      </div>
    </LanguageContext.Provider>
  );
};